import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Avatar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  CameraAlt as CameraAltIcon,
} from "@mui/icons-material";
import { generateSignedUrlForFilename } from "../../utils/s3Utils";

// DocId → Activity Type label
const DOC_TYPE = {
  200: "Start Selfie",
  201: "Stop Selfie",
  300: "2-Hour Selfie",
};

// Color config per activity type
const ROW_COLORS = {
  "Start Selfie": { row: "#e8f5e9", chip: "#2e7d32", text: "#fff" },
  "Stop Selfie": { row: "#fce4ec", chip: "#c62828", text: "#fff" },
  "2-Hour Selfie": { row: "#e3f2fd", chip: "#1565c0", text: "#fff" },
  "Location Sync": { row: "#f3e5f5", chip: "#6a1b9a", text: "#fff" },
};

// Haversine distance in metres
const haversineMetres = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const AssessmentTrackingLog = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [preAssessment, setPreAssessment] = useState(null);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [labLocation, setLabLocation] = useState(null);
  const [activityRows, setActivityRows] = useState([]);
  const [selfieUrls, setSelfieUrls] = useState({ start: null, stop: null });
  const [loading, setLoading] = useState(true);
  const [labProgress, setLabProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  const getParam = (key) => {
    const params = new URLSearchParams(location.search);
    const val = params.get(key);
    return val ? decodeURIComponent(val) : null;
  };

  const appId = getParam("appId");
  const allotedToId = getParam("allotedToId");

  useEffect(() => {
    fetchTrackingData();
    fetchLabProgress();
  }, [appId, allotedToId]);

  const fetchTrackingData = async () => {
    if (!appId || !allotedToId) { setLoading(false); return; }
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/assessmentcontrol/tracking-log?appId=${encodeURIComponent(appId)}&allotedToId=${encodeURIComponent(allotedToId)}`,
        { headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) } }
      );
      if (!response.ok) return;

      const data = await response.json();
      const pre = data.preAssessment && data.preAssessment.length > 0 ? data.preAssessment[0] : null;
      const allot = data.assessmentDetails && data.assessmentDetails.length > 0 ? data.assessmentDetails[0] : null;
      const docs = data.activityLog || [];
      const locs = data.locationLog || [];

      const lab = data.labLocation || null;
      setPreAssessment(pre);
      setAssessmentDetails(allot);
      setLabLocation(lab);

      // Generate S3 signed URLs for all selfie rows (Ins_Dynamic_Doc)
      // Attach lat/long from Ins_PreAssessment for start (200) and stop (201)
      const lLat = lab ? parseFloat(lab.Latitude) : NaN;
      const lLng = lab ? parseFloat(lab.Longitude) : NaN;

      const calcDist = (aLat, aLng) => {
        const fLat = parseFloat(aLat);
        const fLng = parseFloat(aLng);
        return !isNaN(fLat) && !isNaN(fLng) && !isNaN(lLat) && !isNaN(lLng)
          ? haversineMetres(lLat, lLng, fLat, fLng)
          : null;
      };

      const docsWithUrls = await Promise.all(
        docs.map(async (doc) => {
          const docId = Number(doc.DocId);
          let _lat = null;
          let _lng = null;
          if (docId === 200 && pre) { _lat = pre.StartLat; _lng = pre.StartLong; }
          if (docId === 201 && pre) { _lat = pre.StopLat; _lng = pre.StopLong; }
          return {
            ...doc,
            _rowType: "selfie",
            _sortKey: new Date(doc.insertDate).getTime(),
            _lat,
            _lng,
            _distance: calcDist(_lat, _lng),
            signedUrl: doc.imgFile ? await generateSignedUrlForFilename(doc.imgFile).catch(() => null) : null,
          };
        })
      );

      // Location sync rows (Weblocation) — no selfie, has lat/long, calculate distance
      const locRows = locs.map((loc) => ({
        ...loc,
        _rowType: "location",
        _sortKey: new Date(loc.insertdate).getTime(),
        _lat: loc.Applatitude || loc.weblatitude || null,
        _lng: loc.Applongitude || loc.weblongitude || null,
        _distance: calcDist(loc.Applatitude || loc.weblatitude, loc.Applongitude || loc.weblongitude),
      }));

      // Merge and sort by datetime ascending
      const merged = [...docsWithUrls, ...locRows].sort((a, b) => a._sortKey - b._sortKey);
      setActivityRows(merged);

      // Top-section selfie URLs (Start / Stop from Ins_PreAssessment)
      if (pre) {
        const [startUrl, stopUrl] = await Promise.all([
          pre.StartSelfie ? generateSignedUrlForFilename(pre.StartSelfie).catch(() => null) : Promise.resolve(null),
          pre.StopSelfie ? generateSignedUrlForFilename(pre.StopSelfie).catch(() => null) : Promise.resolve(null),
        ]);
        setSelfieUrls({ start: startUrl, stop: stopUrl });
      }
    } catch (err) {
      console.error("Failed to fetch tracking log:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLabProgress = async () => {
    if (!appId || !allotedToId) return;
    setProgressLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getlabdetails?appid=${encodeURIComponent(appId)}&orgid=${encodeURIComponent(allotedToId)}`
      );
      if (!res.ok) return;
      const lab = await res.json();
      if (!lab.isstatus) return;

      let statusMap = {};
      if (lab.assessmentId) {
        try {
          const statusRes = await fetch(
            `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CheckAssessment?assessorId=${encodeURIComponent(allotedToId)}&assessmentId=${encodeURIComponent(lab.assessmentId)}&appId=${encodeURIComponent(appId)}&type=${encodeURIComponent(lab.capacity || "")}`
          );
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.isstatus && Array.isArray(statusData.check)) {
              statusData.check.forEach((item) => {
                statusMap[item.section] = item.status === "True";
              });
            }
          }
        } catch (_) { }
      }

      const [startSelfieUrl, stopSelfieUrl] = await Promise.all([
        lab.startSelfie ? generateSignedUrlForFilename(lab.startSelfie).catch(() => null) : Promise.resolve(null),
        lab.stopSelfie ? generateSignedUrlForFilename(lab.stopSelfie).catch(() => null) : Promise.resolve(null),
      ]);

      setLabProgress({
        capacity: lab.capacity || "",
        startAssessment: lab.startAssessment === "true",
        startAssessmentDate: lab.startAssessmentDate || null,
        stopAssessment: lab.stopAssessment === "true",
        stopAssessmentDate: lab.stopAssessmentDate || null,
        openingMeeting: lab.openingMeeting === "true",
        closingMeeting: lab.closingMeeting === "true",
        applicationReview: statusMap["Application Preview"] !== undefined ? statusMap["Application Preview"] : lab.applicationReview === "true",
        checklist: statusMap["CheckList"] !== undefined ? statusMap["CheckList"] : lab.checklist === "true",
        testWitness: statusMap["Test Witness"] !== undefined ? statusMap["Test Witness"] : lab.testWitness === "true",
        equipment: statusMap["Equipment"] !== undefined ? statusMap["Equipment"] : lab.equipment === "true",
        tada: statusMap["TADA"] !== undefined ? statusMap["TADA"] : lab.tada === "true",
        feedback: statusMap["FeedBack"] !== undefined ? statusMap["FeedBack"] : lab.feedback === "true",
        naf6: statusMap["NAF6"] !== undefined ? statusMap["NAF6"] : lab.naf6 === "true",
        startSelfie: startSelfieUrl,
        stopSelfie: stopSelfieUrl,
      });
    } catch (err) {
      console.error("Failed to fetch lab progress:", err);
    } finally {
      setProgressLoading(false);
    }
  };

  const buildProgressSteps = (lp) => {
    if (!lp) return [];
    const cap = lp.capacity;
    const isLA_LATA = cap === "1" || cap === "3";

    const raw = [
      {
        label: "Start Assessment",
        subLabel: "(Done from mobile app)",
        completed: lp.startAssessment,
        date: lp.startAssessmentDate ? formatDateTime(lp.startAssessmentDate) : null,
        description: "Assessment process initiated",
        selfie: lp.startSelfie,
      },
      ...(isLA_LATA ? [{
        label: "Opening Meeting",
        subLabel: "(Done from mobile app)",
        completed: lp.openingMeeting,
        description: "Initial meeting with lab team",
      }] : []),
      {
        label: "Application Review",
        completed: lp.applicationReview,
        description: "Reviewing application documentation",
      },
      {
        label: "Enter Checklist",
        completed: lp.checklist,
        description: "Complete assessment checklist",
      },
      ...((!isLA_LATA || cap === "3") ? [
        {
          label: "Enter Test Witness",
          completed: lp.testWitness,
          description: "Record test witnessing activities",
        },
        {
          label: "View Equipment Details",
          completed: lp.equipment,
          description: "Review laboratory equipment information",
        },
      ] : []),
      {
        label: "Enter TA DA",
        completed: lp.tada,
        description: "Travel and daily allowance claims",
      },
      ...(isLA_LATA ? [
        {
          label: "Enter Feedback",
          completed: lp.feedback,
          description: "Provide assessment feedback",
        },
        {
          label: "Create NAF 6",
          completed: lp.naf6,
          description: "Generate assessment report",
        },
      ] : [
        {
          label: "Enter Feedback",
          completed: lp.feedback,
          description: "Provide assessment feedback",
        },
      ]),
      {
        label: "Assessment Completed",
        subLabel: "(Done from mobile app)",
        completed: lp.stopAssessment,
        date: lp.stopAssessmentDate ? formatDateTime(lp.stopAssessmentDate) : null,
        description: "Assessment process finalized.",
        selfie: lp.stopSelfie,
      },
      ...(isLA_LATA ? [{
        label: "Closing Meeting",
        subLabel: "(Done from mobile app)",
        completed: lp.closingMeeting,
        description: "Final meeting with lab team",
      }] : []),
    ];

    let foundCurrent = false;
    return raw.map((step) => {
      if (!step.completed && !foundCurrent) {
        foundCurrent = true;
        return { ...step, current: true };
      }
      return { ...step, current: false };
    });
  };

  const getProgressStepIcon = (step, index) => {
    if (step.current) {
      return (
        <Avatar sx={{ bgcolor: "#0b3b8c", color: "white", width: 32, height: 32, boxShadow: `0 0 0 3px ${alpha("#0b3b8c", 0.2)}` }}>
          {index + 1}
        </Avatar>
      );
    }
    if (step.completed) {
      return (
        <Avatar sx={{ bgcolor: "#2e7d32", color: "white", width: 32, height: 32 }}>
          <CheckCircleIcon fontSize="small" />
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ bgcolor: "#bdbdbd", color: "#757575", width: 32, height: 32 }}>
        {index + 1}
      </Avatar>
    );
  };

  const formatDate = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatTime = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const formatDateTime = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  };

  const pre = preAssessment || {};
  const allot = assessmentDetails || {};

  // Latest 2-hour selfie = last DocId 300 row from selfie entries
  const twoHourRows = activityRows.filter((r) => r._rowType === "selfie" && (Number(r.DocId) === 300));
  const latestTwoHour = twoHourRows.length > 0 ? twoHourRows[twoHourRows.length - 1] : null;

  const details = {
    assessmentNo: allot.AssessorAllotmentId || pre.assessmentid || "-",
    applicationNo: pre.ApplicationNo || allot.AppId || appId || "-",
    laboratoryName: pre.LaboratoryName || "-",
    assessorName: pre.LoginName || allot.LoginName || "-",
    assessmentDate: formatDate(pre.StartDate || allot.DateFrom),
    startTime: formatTime(pre.StartDate),
    endTime: formatTime(pre.StopDate || allot.DateTo),
    startLat: pre.StartLat || "-",
    startLong: pre.StartLong || "-",
    stopLat: pre.StopLat || "-",
    stopLong: pre.StopLong || "-",
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const SelfieBox = ({ title, src, time }) => (
    <Box sx={{ flex: 1, textAlign: "center" }}>
      <Typography sx={{ fontSize: 16, fontWeight: "bold", color: "#0b3b8c", mb: 1 }}>
        {title}
      </Typography>
      {src ? (
        <Box
          component="img"
          src={src}
          alt={title}
          sx={{ width: "100%", height: 220, objectFit: "cover", borderRadius: 2, border: "1px solid #ddd" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%", height: 220, borderRadius: 2, border: "1px solid #ddd",
            backgroundColor: "#f0f4fa", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Typography color="text.secondary" fontSize={13}>No image</Typography>
        </Box>
      )}
      <Typography sx={{ mt: 1, fontSize: 14, color: "#555" }}>{time || "-"}</Typography>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: "#f5f7fb", minHeight: "100vh", p: 2.5 }}>
      <Box sx={{ maxWidth: 1600, mx: "auto" }}>

        {/* Header */}
        <Box
          sx={{
            background: "#0b3b8c", color: "white", p: "18px 25px", borderRadius: 2, mb: 2,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "white" }}>
            Assessment Tracking Log
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: "white", borderColor: "white", "&:hover": { borderColor: "#ccc", backgroundColor: "rgba(255,255,255,0.1)" } }}
          >
            Back
          </Button>
        </Box>

        {/* Assessment Details + Selfies */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ border: "1px solid #d9e1f2", borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,.08)", height: "100%" }}>
              <Typography sx={{ color: "#0b3b8c", fontSize: 20, fontWeight: "bold", p: "15px 20px", borderBottom: "1px solid #e5e5e5" }}>
                Assessment Details
              </Typography>
              <CardContent sx={{ p: 0 }}>
                <Table size="small">
                  <TableBody>
                    {[
                      ["Application No.", details.applicationNo],
                      ["Laboratory Name", details.laboratoryName],
                      ["Assessor", details.assessorName],
                      ["Assessment Date", details.assessmentDate],
                      ["Assessment Start Time", details.startTime],
                      ["Assessment End Time", details.endTime],
                    ].map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 14, width: "45%", py: 1.5, px: 2.5 }}>
                          {label}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, py: 1.5, px: 2.5 }}>{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ border: "1px solid #d9e1f2", borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,.08)", height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <SelfieBox
                    title="Start Selfie"
                    src={selfieUrls.start}
                    time={formatDateTime(pre.StartDate)}
                  />
                  <SelfieBox
                    title="Latest 2-Hour Selfie"
                    src={latestTwoHour?.signedUrl || null}
                    time={formatDateTime(latestTwoHour?.insertDate)}
                  />
                  <SelfieBox
                    title="Stop Selfie"
                    src={selfieUrls.stop}
                    time={formatDateTime(pre.StopDate)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Assessor Start Location */}
        <Box sx={{ mb: 2, width: { xs: "100%", md: "35%" } }}>
          <Card sx={{ border: "1px solid #d9e1f2", borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,.08)" }}>
            <Typography sx={{ color: "#0b3b8c", fontSize: 20, fontWeight: "bold", p: "15px 20px", borderBottom: "1px solid #e5e5e5" }}>
              Laboratory Reference Location
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 14, width: "40%", py: 1.5, px: 2.5 }}>Latitude</TableCell>
                  <TableCell sx={{ fontSize: 14, py: 1.5, px: 2.5 }}>{labLocation?.Latitude || "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 14, width: "40%", py: 1.5, px: 2.5 }}>Longitude</TableCell>
                  <TableCell sx={{ fontSize: 14, py: 1.5, px: 2.5 }}>{labLocation?.Longitude || "-"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Box>

        <Grid container spacing={2} >
           <Grid item sm={8} xs={12} sx={{pr: 1 }} >
            {/* Activity Log Table */}
            <Card sx={{ border: "1px solid #d9e1f2", borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" }}>
              <Typography sx={{ color: "#0b3b8c", fontSize: 20, fontWeight: "bold", p: "15px 20px" }}>
                Assessment Activity Log
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: "none", maxHeight: 480, overflowY: "auto",  }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#0b3b8c" }}>
                      {["Sr.", "Activity Type", "Date & Time", "Assessor Latitude", "Assessor Longitude", "Distance From Lab (m)", "Selfie"].map((h) => (
                        <TableCell key={h} sx={{ color: "white", fontWeight: "bold", fontSize: 14, textAlign: "center" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          No activity log data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      activityRows.map((row, idx) => {
                        const isLocation = row._rowType === "location";
                        const activityType = isLocation
                          ? "Location Sync"
                          : (DOC_TYPE[Number(row.DocId)] || `Doc ${row.DocId}`);
                        const dateTime = isLocation ? row.insertdate : row.insertDate;
                        const lat = row._lat || "-";
                        const lng = row._lng || "-";
                        const colors = ROW_COLORS[activityType] || { row: "#fff", chip: "#555", text: "#fff" };

                        return (
                          <TableRow
                            key={row.Id || row.id || idx}
                            sx={{
                              backgroundColor: colors.row,
                              "&:hover": { filter: "brightness(0.96)" },
                              borderBottom: "1px solid #e8e8e8",
                            }}
                          >
                            <TableCell align="center" sx={{ fontSize: 14, fontWeight: 600 }}>{idx + 1}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={activityType}
                                size="small"
                                sx={{
                                  backgroundColor: colors.chip,
                                  color: colors.text,
                                  fontWeight: 600,
                                  fontSize: 12,
                                  borderRadius: "6px",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: 14 }}>{formatDateTime(dateTime)}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 14 }}>{lat}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 14 }}>{lng}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 14 }}>
                              {row._distance !== null && row._distance !== undefined ? row._distance : "-"}
                            </TableCell>
                            <TableCell align="center">
                              {!isLocation && row.signedUrl ? (
                                <Box
                                  component="img"
                                  src={row.signedUrl}
                                  alt={activityType}
                                  sx={{ width: 70, height: 50, borderRadius: 1, objectFit: "cover", cursor: "pointer" }}
                                  onClick={() => window.open(row.signedUrl, "_blank")}
                                />
                              ) : (
                                <Typography fontSize={14}>-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: "15px 20px", fontSize: 13, backgroundColor: "#fafafa", borderTop: "1px solid #e5e5e5", color: "#555" }}>
                Activity log from assessment selfie captures. Click any selfie to view full image.
              </Box>
            </Card>
          </Grid>
          <Grid item sm={4} xs={12} >
            {/* Assessment Progress */}
            <Card sx={{ border: "1px solid #d9e1f2", borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,.08)", mb: 2 }}>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <TrendingUpIcon sx={{ color: "#0b3b8c" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0b3b8c" }}>
                        Assessment Progress
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => fetchLabProgress()}
                      disabled={progressLoading}
                      size="small"
                      sx={{ color: "#0b3b8c", "&:hover": { bgcolor: alpha("#0b3b8c", 0.08) } }}
                      startIcon={
                        <RefreshIcon
                          sx={{
                            fontSize: 18,
                            animation: progressLoading ? "spin 1s linear infinite" : "none",
                            "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
                          }}
                        />
                      }
                    >
                      Refresh
                    </Button>
                  </Box>
                }
                sx={{ borderBottom: "1px solid #e5e5e5", pb: 1 }}
              />
              <CardContent sx={{ p: 3 }}>
                {progressLoading && !labProgress ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : !labProgress ? (
                  <Typography color="text.secondary" fontSize={14}>Progress data unavailable.</Typography>
                ) : (
                  <Box sx={{ maxHeight: 480, overflowY: "auto", pr: 1 }}>
                    {buildProgressSteps(labProgress).map((step, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 0.5,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: step.current ? alpha("#0b3b8c", 0.05) : "transparent",
                          border: step.current ? `2px solid ${alpha("#0b3b8c", 0.2)}` : "none",
                          transition: "all 0.2s ease",
                          "&:hover": { backgroundColor: alpha("#0b3b8c", 0.03) },
                        }}
                      >
                        <Box sx={{ mr: 2, flexShrink: 0 }}>{getProgressStepIcon(step, index)}</Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: step.current ? "#0b3b8c" : step.completed ? "#2e7d32" : "#616161",
                              mb: step.subLabel ? 0 : 0.5,
                            }}
                          >
                            {step.label}
                          </Typography>
                          {step.subLabel && (
                            <Typography variant="caption" sx={{ color: "#9e9e9e", fontStyle: "italic", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                              {step.subLabel}
                            </Typography>
                          )}
                          {step.description && (
                            <Typography variant="body2" sx={{ color: "#757575", fontSize: "0.8rem", mb: step.date ? 0.5 : 0 }}>
                              {step.description}
                            </Typography>
                          )}
                          {step.date && (
                            <Typography variant="caption" sx={{ color: "#9e9e9e", fontWeight: 500, display: "block", mb: step.selfie ? 1 : 0 }}>
                              {step.date}
                            </Typography>
                          )}
                          {step.selfie && (
                            <Box
                              sx={{
                                display: "inline-flex", alignItems: "center", gap: 1, p: 1, mt: 0.5,
                                borderRadius: 2, bgcolor: alpha("#0b3b8c", 0.05),
                                border: `1px solid ${alpha("#0b3b8c", 0.2)}`,
                                cursor: "pointer", transition: "all 0.2s",
                                "&:hover": { bgcolor: alpha("#0b3b8c", 0.1), transform: "scale(1.02)", boxShadow: 2 },
                              }}
                              onClick={() => window.open(step.selfie, "_blank")}
                            >
                              <Box sx={{ width: 50, height: 50, borderRadius: 1, overflow: "hidden", border: `2px solid #0b3b8c` }}>
                                <img src={step.selfie} alt="Selfie" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: "#0b3b8c", fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <CameraAltIcon sx={{ fontSize: 14 }} /> View Selfie
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
         
        </Grid>




      </Box>
    </Box>
  );
};

export default AssessmentTrackingLog;
